import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  QueryConstraint,
  getDocs,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { VehicleBooking, BookingStatus } from '../types/vehicle';
import * as bookingService from '../services/vehicleBookingService';

interface UseVehicleBookingsOptions {
  vehicleId?: string;
  status?: BookingStatus;
  realtime?: boolean;
}

export const useVehicleBookings = (options: UseVehicleBookingsOptions = {}) => {
  const { currentUser } = useAuth();
  const organizationId = currentUser?.organizationId;
  const [bookings, setBookings] = useState<VehicleBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { vehicleId, status, realtime = true } = options;

  // Load bookings
  const loadBookings = useCallback(async () => {
    if (!organizationId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const filters: any = {};
      if (vehicleId) filters.vehicleId = vehicleId;
      if (status) filters.status = status;

      const data = await bookingService.getBookings(organizationId, filters);
      setBookings(data);
    } catch (err: any) {
      console.error('Error loading bookings:', err);
      setError(err.message || 'Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }, [organizationId, vehicleId, status]);

  // Real-time listener
  useEffect(() => {
    if (!organizationId || !realtime) {
      if (!realtime) loadBookings();
      return;
    }

    // Először lekérjük az organization járműveit
    const loadBookingsWithVehicles = async () => {
      try {
        const vehiclesQuery = query(
          collection(db, 'vehicles'),
          where('organizationId', '==', organizationId)
        );
        const vehiclesSnapshot = await getDocs(vehiclesQuery);
        const vehicleIds = vehiclesSnapshot.docs.map(doc => doc.id);

        if (vehicleIds.length === 0) {
          setBookings([]);
          setLoading(false);
          return;
        }

        // Most lekérjük a foglalásokat ezekhez a járművekhez
        const constraints: QueryConstraint[] = [
          where('vehicleId', 'in', vehicleIds.slice(0, 10)), // Firestore max 10 elem
          orderBy('startDate', 'desc'),
        ];

        if (vehicleId) {
          constraints.length = 0;
          constraints.push(where('vehicleId', '==', vehicleId));
          constraints.push(orderBy('startDate', 'desc'));
        }

        if (status) {
          constraints.push(where('status', '==', status));
        }

        const q = query(collection(db, 'vehicleBookings'), ...constraints);

        const unsubscribe = onSnapshot(
          q,
          (snapshot) => {
            const data = snapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            })) as VehicleBooking[];
            setBookings(data);
            setLoading(false);
            setError(null);
          },
          (err) => {
            console.error('Error in bookings listener:', err);
            setError(err.message);
            setLoading(false);
          }
        );

        return unsubscribe;
      } catch (err: any) {
        console.error('Error loading bookings:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    loadBookingsWithVehicles();
  }, [organizationId, vehicleId, status, realtime, loadBookings]);

  // Create booking
  const createBooking = useCallback(
    async (bookingData: Omit<VehicleBooking, 'id' | 'createdAt' | 'updatedAt' | 'organizationId'>) => {
      if (!organizationId) throw new Error('Organization ID is required');

      try {
        const dataWithOrg = { ...bookingData, organizationId };
        const id = await bookingService.createBooking(dataWithOrg as any, organizationId);
        return id;
      } catch (err: any) {
        console.error('Error creating booking:', err);
        throw err;
      }
    },
    [organizationId]
  );

  // Update booking
  const updateBooking = useCallback(
    async (id: string, updates: Partial<VehicleBooking>) => {
      try {
        await bookingService.updateBooking(id, updates);
      } catch (err: any) {
        console.error('Error updating booking:', err);
        throw err;
      }
    },
    []
  );

  // Update booking status
  const updateStatus = useCallback(
    async (id: string, newStatus: BookingStatus) => {
      try {
        await bookingService.updateBookingStatus(id, newStatus, currentUser?.uid);
      } catch (err: any) {
        console.error('Error updating booking status:', err);
        throw err;
      }
    },
    [currentUser]
  );

  // Delete booking
  const deleteBooking = useCallback(async (id: string) => {
    try {
      await bookingService.deleteBooking(id);
    } catch (err: any) {
      console.error('Error deleting booking:', err);
      throw err;
    }
  }, []);

  // Check vehicle availability
  const checkAvailability = useCallback(
    async (vehicleId: string, startDate: Date, endDate: Date, excludeBookingId?: string) => {
      if (!organizationId) return false;

      try {
        return await bookingService.isVehicleAvailable(
          vehicleId,
          startDate,
          endDate,
          organizationId,
          excludeBookingId
        );
      } catch (err: any) {
        console.error('Error checking availability:', err);
        throw err;
      }
    },
    [organizationId]
  );

  return {
    bookings,
    loading,
    error,
    loadBookings,
    createBooking,
    updateBooking,
    updateStatus,
    deleteBooking,
    checkAvailability,
  };
};

// Hook for upcoming bookings
export const useUpcomingBookings = (days: number = 30) => {
  const { currentUser } = useAuth();
  const organizationId = currentUser?.organizationId;
  const [bookings, setBookings] = useState<VehicleBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!organizationId) {
      setLoading(false);
      return;
    }

    const loadUpcoming = async () => {
      try {
        setLoading(true);
        const data = await bookingService.getUpcomingBookings(organizationId, days);
        setBookings(data);
        setError(null);
      } catch (err: any) {
        console.error('Error loading upcoming bookings:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadUpcoming();
  }, [organizationId, days]);

  return { bookings, loading, error };
};

// Hook for active bookings
export const useActiveBookings = () => {
  const { currentUser } = useAuth();
  const organizationId = currentUser?.organizationId;
  const [bookings, setBookings] = useState<VehicleBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!organizationId) {
      setLoading(false);
      return;
    }

    const loadActive = async () => {
      try {
        setLoading(true);
        const data = await bookingService.getActiveBookings(organizationId);
        setBookings(data);
        setError(null);
      } catch (err: any) {
        console.error('Error loading active bookings:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadActive();
  }, [organizationId]);

  return { bookings, loading, error };
};
